import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { 
  collection, doc, getDoc, updateDoc, setDoc, getDocs, query, where, arrayUnion, arrayRemove, serverTimestamp, documentId, limit
} from "firebase/firestore";

import AppHeader from "./AppHeader";  
import MobileHistoryPanel from "./MobileHistoryPanel";
import "./UserProfilePanel.css";

import QuizBattlesTab from "./QuizBattlesTab";

export default function UserProfilePanel(props) {
  const {
    currentUser,
    setShowHistoryMobile,
    showFeedback,
    feedbackText,
    setFeedbackText,
    feedbackType,
    setFeedbackType,
    setShowFeedback,
    handleSubmitFeedback,
    showFeedbackSuccess,
    onHomeClick,
    showHistoryMobile: mobileHistoryState,
    sessions,
    currentSessionId,
    confirmingDelete,
    setConfirmingDelete,
    openSession,
    deleteSession,
    groupSessionsByDate,
    lastUserQuestionSafe,
    getSubjectIcon,
    showSocialMenu,
    setShowSocialMenu,
    socialMenuRef,
    newSessions,
    openDeleteAllModal,
    confirmDeleteAll,
    closeDeleteAllModal,
    DangerModal,
    onOpenChat
  } = props;

  const navigate = useNavigate();
 
  const [activeTab, setActiveTab] = useState("profile"); // 'profile' or 'discover'
  const [profileSubTab, setProfileSubTab] = useState("overview"); // 'overview', 'followers', 'following'
  
  // Floating Chat Widget State
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { sender: "other", text: "But intha policy ippo tha vanthathu" },
    { sender: "me", text: "Avanga kitta neenga ketkalaamla bro" },
    { sender: "other", text: "Many channel poi irukku" },
    { sender: "other", text: "No use bro, policy face or voice kodukkanum, original content irukkanum" },
    { sender: "other", text: "Athu mattum tha , vers options illa" },
    { sender: "me", text: "Neenga parthu pannunga" },
    { sender: "other", text: "Ok bro" }
  ]);
  const [chatInputValue, setChatInputValue] = useState("");

  const handleSendChatMessage = (e) => {
    e.preventDefault();
    if (!chatInputValue.trim()) return;
    setChatMessages(prev => [...prev, { sender: "me", text: chatInputValue.trim() }]);
    setChatInputValue("");
  };
  
  // Full Firestore User Structure State
  const [profileData, setProfileData] = useState({
    uid: "",
    name: "",
    email: "",
    photoURL: "",
    username: "",
    location: "",
    institution: "",
    bio: "",
    website: "",
    role: "Student",
    points: 0,
    streak: 0,
    badges: [],
    followers: [],
    following: [],
    joinedAt: "",
    lastLogin: "",
    updatedAt: ""
  });

  const [initialUsername, setInitialUsername] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [visibleCount, setVisibleCount] = useState(10);
  const [loadingMore, setLoadingMore] = useState(false);

  // Username Availability Checking States
  const [usernameStatus, setUsernameStatus] = useState("available"); // 'available', 'taken'
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);

  const [visibleFollowersCount, setVisibleFollowersCount] = useState(10);
  const [visibleFollowingCount, setVisibleFollowingCount] = useState(10);

  // Toast / Alert Notification State
  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  // Fetch initial suggestions (limited to 10)
  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        if (!currentUser) return;
        
        const q = query(
          collection(db, "users"),
          limit(10) // Restrict initial load to 10 records
        );
        
        const querySnapshot = await getDocs(q);
        const usersList = [];
        querySnapshot.forEach((docSnap) => {
          if (docSnap.id !== currentUser.uid) {
            usersList.push({ id: docSnap.id, ...docSnap.data() });
          }
        });
        
        setSuggestions(usersList);
        setVisibleCount(10); // Reset visible count on fresh fetch
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      }
    };

    fetchSuggestions();
  }, [currentUser]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    // Simulate a brief smooth loading state or directly expand state
    setTimeout(() => {
      setVisibleCount(prev => prev + 10);
      setLoadingMore(false);
    }, 300);
  };

  // Discover & Search states
  const [suggestions, setSuggestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // Detailed Connection Lists Data
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);

  // Helper to format timestamps gracefully
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    if (typeof timestamp === "string") return timestamp;
    if (timestamp?.toDate) {
      return timestamp.toDate().toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    }
    if (timestamp?.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    }
    return "N/A";
  };

  // Fetch current user details from Firestore
 useEffect(() => {
    if (!currentUser) return;

    const fetchUserData = async () => {
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          const fetchedUsername = data.username || "";
          
          // Use prev to merge database values without wiping out unwritten fields
          setProfileData(prev => ({
            ...prev,
            uid: currentUser.uid,
            name: data.name || currentUser.displayName || prev.name || "",
            email: data.email || currentUser.email || prev.email || "",
            photoURL: data.photoURL || currentUser.photoURL || prev.photoURL || "",
            username: fetchedUsername || prev.username || "",
            location: data.location ?? prev.location ?? "",
            institution: data.institution ?? prev.institution ?? "",
            bio: data.bio ?? prev.bio ?? "",
            website: data.website ?? prev.website ?? "",
            role: data.role || prev.role || "Student",
            points: data.points ?? prev.points ?? 0,
            streak: data.streak ?? prev.streak ?? 0,
            badges: Array.isArray(data.badges) ? data.badges : prev.badges,
            followers: Array.isArray(data.followers) ? data.followers : prev.followers,
            following: Array.isArray(data.following) ? data.following : prev.following,
            joinedAt: data.joinedAt || prev.joinedAt || "",
            lastLogin: data.lastLogin || prev.lastLogin || "",
            updatedAt: data.updatedAt || prev.updatedAt || ""
          }));
          setInitialUsername(fetchedUsername);
        } else {
          setProfileData(prev => ({
            ...prev,
            uid: currentUser.uid,
            name: currentUser.displayName || "",
            email: currentUser.email || "",
            photoURL: currentUser.photoURL || ""
          }));
        }
      } catch (error) {
        console.error("UserProfilePanel: Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, [currentUser]);

  // Fetch Followers and Following detailed user objects with deduplication
  useEffect(() => {
    const fetchConnectionsDetails = async () => {
      try {
        if (!currentUser) return;

        // Deduplicate and filter out own ID or empty values
        const uniqueFollowers = [...new Set(profileData.followers)].filter(id => id && id !== currentUser.uid);
        const uniqueFollowing = [...new Set(profileData.following)].filter(id => id && id !== currentUser.uid);

        // Fetch Followers
        if (uniqueFollowers.length > 0) {
          const fetchedFollowers = [];
          const chunks = [];
          for (let i = 0; i < uniqueFollowers.length; i += 30) {
            chunks.push(uniqueFollowers.slice(i, i + 30));
          }
          for (const chunk of chunks) {
            const q = query(collection(db, "users"), where(documentId(), "in", chunk));
            const snap = await getDocs(q);
            snap.forEach(docSnap => fetchedFollowers.push({ id: docSnap.id, ...docSnap.data() }));
          }
          setFollowersList(fetchedFollowers);
          setVisibleFollowersCount(10);
        } else {
          setFollowersList([]);
        }

        // Fetch Following
        if (uniqueFollowing.length > 0) {
          const fetchedFollowing = [];
          const chunks = [];
          for (let i = 0; i < uniqueFollowing.length; i += 30) {
            chunks.push(uniqueFollowing.slice(i, i + 30));
          }
          for (const chunk of chunks) {
            const q = query(collection(db, "users"), where(documentId(), "in", chunk));
            const snap = await getDocs(q);
            snap.forEach(docSnap => fetchedFollowing.push({ id: docSnap.id, ...docSnap.data() }));
          }
          setFollowingList(fetchedFollowing);
          setVisibleFollowingCount(10);
        } else {
          setFollowingList([]);
        }
      } catch (error) {
        console.error("Error fetching connection details:", error);
      }
    };

    fetchConnectionsDetails();
  }, [profileData.followers, profileData.following, currentUser]);

  // Handle username check logic as user types
  const handleUsernameChange = async (e) => {
    const val = e.target.value.toLowerCase().replace(/\s+/g, '');
    setProfileData(prev => ({ ...prev, username: val }));

    if (!val || val.length < 3) {
      setUsernameStatus("available");
      return;
    }

    if (val === initialUsername) {
      setUsernameStatus("available");
      return;
    }

    setIsCheckingUsername(true);
    try {
      const q = query(collection(db, "users"), where("username", "==", val));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setUsernameStatus("taken");
      } else {
        setUsernameStatus("available");
      }
    } catch (error) {
      console.error("Error checking username availability:", error);
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const getRoleBadgeStyle = (role) => {
  switch ((role || "").toLowerCase()) {
    case "mentor":
    case "educator":
      return { backgroundColor: "#dbeafe", color: "#1e40af" }; // Blue
    case "professional":
      return { backgroundColor: "#f3e8ff", color: "#6b21a8" }; // Purple
    case "researcher":
      return { backgroundColor: "#ccfbf1", color: "#115e59" }; // Teal
    case "student":
    default:
      return { backgroundColor: "#ffedd5", color: "#c2410c" }; // Orange/Default
  }
};

 // Handle saving editable fields using setDoc with merge to prevent missing document errors
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    if (usernameStatus === "taken") {
      showToast("Please choose an available username before saving.", "error");
      return;
    }

    setIsSaving(true);
    try {
      const userRef = doc(db, "users", currentUser.uid);
      
      const updatedFields = {
        uid: currentUser.uid,
        email: profileData.email || currentUser.email || "",
        photoURL: profileData.photoURL || currentUser.photoURL || "",
        name: profileData.name,
        username: profileData.username,
        location: profileData.location,
        institution: profileData.institution,
        bio: profileData.bio,
        website: profileData.website,
        role: profileData.role,
        updatedAt: serverTimestamp()
      };

      // Use setDoc with merge: true so it creates the document if missing or updates it if present
      await setDoc(userRef, updatedFields, { merge: true });
      
      setProfileData(prev => ({
        ...prev,
        ...updatedFields,
        updatedAt: new Date().toISOString()
      }));

      setInitialUsername(profileData.username);
      setIsEditing(false);
      showToast("Profile updated successfully!", "success");
    } catch (error) {
      console.error("UserProfilePanel: Failed to update profile:", error);
      showToast("Failed to update profile. Check console for details.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Fetch and score recommendations for Discover tab
  useEffect(() => {
    if (activeTab === "discover" && currentUser) {
      const fetchDiscoverUsers = async () => {
        try {
          const querySnapshot = await getDocs(collection(db, "users"));
          let usersList = [];
          
          querySnapshot.forEach((docSnap) => {
            if (docSnap.id !== currentUser.uid) {
              const data = docSnap.data();
              let score = 0;
              
              if (profileData.location && data.location && data.location.toLowerCase() === profileData.location.toLowerCase()) {
                score += 2;
              }
              if (profileData.institution && data.institution && data.institution.toLowerCase() === profileData.institution.toLowerCase()) {
                score += 3;
              }
              if (profileData.role && data.role && data.role.toLowerCase() === profileData.role.toLowerCase()) {
                score += 1;
              }

              usersList.push({ id: docSnap.id, ...data, matchScore: score });
            }
          });

          usersList.sort((a, b) => b.matchScore - a.matchScore);
          setSuggestions(usersList);
        } catch (error) {
          console.error("Error fetching discover users:", error);
        }
      };
      fetchDiscoverUsers();
    }
  }, [activeTab, currentUser?.uid, profileData?.location, profileData?.institution, profileData?.role]);

  // Robust Client-Side Search (Avoids Firestore index errors and supports partial matching)
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    const queryTerm = searchQuery.toLowerCase().trim();

    try {
      const querySnapshot = await getDocs(collection(db, "users"));
      let results = [];
      
      querySnapshot.forEach((docSnap) => {
        if (docSnap.id !== currentUser?.uid) {
          const data = docSnap.data();
          const username = (data.username || "").toLowerCase();
          const name = (data.name || "").toLowerCase();

          if (username.includes(queryTerm) || name.includes(queryTerm)) {
            results.push({ id: docSnap.id, ...data });
          }
        }
      });

      setSearchResults(results);
      if (results.length === 0) {
        showToast("No users found matching that name or username.", "error");
      }
    } catch (error) {
      console.error("UserProfilePanel: Search failed:", error);
      showToast("Search failed. Check console for details.", "error");
    }
  };

  // Toggle follow state with instant UI state updates and self-action guard
  const handleToggleFollow = async (targetUserId, isCurrentlyFollowing) => {
    if (!currentUser || targetUserId === currentUser.uid) return;
    try {
      const targetRef = doc(db, "users", targetUserId);
      const myRef = doc(db, "users", currentUser.uid);

      const updateListState = (list, targetId, add = true) => {
        if (add) {
          return list.includes(targetId) ? list : [...list, targetId];
        } else {
          return list.filter(id => id !== targetId);
        }
      };

      if (isCurrentlyFollowing) {
        await updateDoc(targetRef, { followers: arrayRemove(currentUser.uid) });
        await updateDoc(myRef, { following: arrayRemove(targetUserId) });

        setProfileData(prev => ({
          ...prev,
          following: updateListState(prev.following, targetUserId, false)
        }));

        setSuggestions(prev => prev.map(u => u.id === targetUserId ? { ...u, followers: updateListState(u.followers || [], currentUser.uid, false) } : u));
        setSearchResults(prev => prev.map(u => u.id === targetUserId ? { ...u, followers: updateListState(u.followers || [], currentUser.uid, false) } : u));
      } else {
        await updateDoc(targetRef, { followers: arrayUnion(currentUser.uid) });
        await updateDoc(myRef, { following: arrayUnion(targetUserId) });

        setProfileData(prev => ({
          ...prev,
          following: updateListState(prev.following, targetUserId, true)
        }));

        setSuggestions(prev => prev.map(u => u.id === targetUserId ? { ...u, followers: updateListState(u.followers || [], currentUser.uid, true) } : u));
        setSearchResults(prev => prev.map(u => u.id === targetUserId ? { ...u, followers: updateListState(u.followers || [], currentUser.uid, true) } : u));
      }
    } catch (error) {
      console.error("UserProfilePanel: Failed to toggle follow state:", error);
      showToast("Failed to update follow state.", "error");
    }
  };

  if (!currentUser) {
    return (
      <div className="up-loading-container">
        <div className="up-loading-spinner"></div>
        <p className="up-loading-text">Loading user profile...</p>
      </div>
    );
  }

  const defaultAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    profileData.name || "User"
  )}&background=f97316&color=fff&bold=true`;

  return (
    <div className="up-layout">
      {/* Header */}
      <AppHeader 
        setShowHistoryMobile={setShowHistoryMobile}
        showFeedback={showFeedback}
        feedbackText={feedbackText}
        setFeedbackText={setFeedbackText}
        feedbackType={feedbackType}
        setFeedbackType={setFeedbackType}
        setShowFeedback={setShowFeedback}
        handleSubmitFeedback={handleSubmitFeedback}
        showFeedbackSuccess={showFeedbackSuccess}
        onHomeClick={onHomeClick || (() => navigate("/"))}
      />

      <main className="up-main-container">
        {/* Navigation Tabs Header */}
        <div className="up-hero-banner">
          <div className="up-hero-content">
            <h1 className="up-hero-title">User Hub</h1>
            <p className="up-hero-subtitle">
              Manage your academic identity, track your connections, and discover study peers.
            </p>
          </div>
          
          <div className="up-tab-nav">
            <button 
              onClick={() => setActiveTab("profile")} 
              className={`up-tab-button ${activeTab === "profile" ? "active" : ""}`}
            >
              👤 My Profile
            </button>
            <button 
              onClick={() => setActiveTab("discover")} 
              className={`up-tab-button ${activeTab === "discover" ? "active" : ""}`}
            >
              🔍 Discover & Connect
            </button> 
          </div>
        </div>

        {/* Tab 1: Profile Details */}
        {activeTab === "profile" && (
          <div className="up-panel-card">
            {/* Top User Header Card */}
            <div className="up-profile-header-card">
              <div className="up-avatar-wrapper">
                <img 
                  src={profileData.photoURL || defaultAvatar} 
                  alt={profileData.name || "Profile"} 
                  className="up-avatar-img"
                  onError={(e) => { e.target.src = defaultAvatar; }}
                />
              </div>

              <div className="up-header-details">
                <div className="up-header-top-row">
                  <div>
                    <h2 className="up-user-name">{profileData.name || "Unnamed User"}</h2>
                    <div className="up-user-handles">
                      <span className="up-username">@{profileData.username || "username_not_set"}</span>
                      {profileData.role && (
                        <span className="up-role-badge" style={getRoleBadgeStyle(profileData.role)}>
                            {profileData.role}
                        </span>
                        )}
                    </div>
                  </div>
                  
                  {!isEditing && (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button onClick={() => setIsEditing(true)} className="up-primary-btn">
                        ✏️ Edit Profile
                      </button>
                      <button 
  onClick={onOpenChat} 
  className="up-secondary-btn" 
  style={{ display: "inline-flex", alignItems: "center", gap: "4px", cursor: "pointer" }}
>
  💬 Chat
</button>
                    </div>
                  )}
                </div>

                {profileData.bio && <p className="up-bio-text">{profileData.bio}</p>}

                {profileData.website && (
                  <a 
                    href={profileData.website.startsWith("http") ? profileData.website : `https://${profileData.website}`} 
                    target="_blank" 
                    rel="noreferrer" 
                    className="up-website-link"
                  >
                    🔗 {profileData.website}
                  </a>
                )}
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="up-stats-bar">
              <div className="up-stat-box">
                <span className="up-stat-val">⚡ {profileData.points}</span>
                <span className="up-stat-lbl">Points</span>
              </div>
              <div className="up-stat-box">
                <span className="up-stat-val">🔥 {profileData.streak}</span>
                <span className="up-stat-lbl">Day Streak</span>
              </div>
              <div className="up-stat-box">
                <span className="up-stat-val">🏅 {profileData.badges.length}</span>
                <span className="up-stat-lbl">Badges</span>
              </div> 
            </div>

            {/* Profile Sub-Navigation */}
            {!isEditing && (
                <div style={{ display: "flex", gap: "12px", borderBottom: "1px solid #e2e8f0", marginBottom: "24px" }}>
                    <button
                    onClick={() => setProfileSubTab("overview")}
                    style={{
                        background: "none",
                        border: "none",
                        padding: "10px 16px",
                        fontWeight: profileSubTab === "overview" ? "600" : "400",
                        color: profileSubTab === "overview" ? "#f97316" : "#64748b",
                        borderBottom: profileSubTab === "overview" ? "2px solid #f97316" : "2px solid transparent",
                        cursor: "pointer"
                    }}
                    >
                    Overview
                    </button>
                    <button
                    onClick={() => setProfileSubTab("followers")}
                    style={{
                        background: "none",
                        border: "none",
                        padding: "10px 16px",
                        fontWeight: profileSubTab === "followers" ? "600" : "400",
                        color: profileSubTab === "followers" ? "#f97316" : "#64748b",
                        borderBottom: profileSubTab === "followers" ? "2px solid #f97316" : "2px solid transparent",
                        cursor: "pointer"
                    }}
                    >
                    Followers ({profileData.followers.length})
                    </button>
                    <button
                    onClick={() => setProfileSubTab("following")}
                    style={{
                        background: "none",
                        border: "none",
                        padding: "10px 16px",
                        fontWeight: profileSubTab === "following" ? "600" : "400",
                        color: profileSubTab === "following" ? "#f97316" : "#64748b",
                        borderBottom: profileSubTab === "following" ? "2px solid #f97316" : "2px solid transparent",
                        cursor: "pointer"
                    }}
                    >
                    Following ({profileData.following.length})
                    </button>
                    <button
                    onClick={() => setProfileSubTab("battles")}
                    style={{
                        background: "none",
                        border: "none",
                        padding: "10px 16px",
                        fontWeight: profileSubTab === "battles" ? "600" : "400",
                        color: profileSubTab === "battles" ? "#f97316" : "#64748b",
                        borderBottom: profileSubTab === "battles" ? "2px solid #f97316" : "2px solid transparent",
                        cursor: "pointer"
                    }}
                    >
                    ⚔️ Battles
                    </button>
                </div>
                )}
            {/* Form Mode vs Display Mode */}
            {isEditing ? (
              <form onSubmit={handleSaveProfile} className="up-form-container">
                <h3 className="up-section-title">Edit Profile Information</h3>
                
                <div className="up-form-grid">
                  <div className="up-input-group">
                    <label className="up-label">Full Name</label>
                    <input 
                      type="text" 
                      value={profileData.name} 
                      onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                      required
                      className="up-input"
                      placeholder="e.g. Raja Maran"
                    />
                  </div>

                  <div className="up-input-group">
                    <label className="up-label">Username</label>
                    <input 
                      type="text" 
                      value={profileData.username} 
                      onChange={handleUsernameChange}
                      required
                      className="up-input"
                      placeholder="e.g. raja"
                    />
                    {isCheckingUsername && (
                      <span style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>Checking availability...</span>
                    )}
                    {!isCheckingUsername && usernameStatus === "taken" && (
                      <span style={{ fontSize: "12px", color: "#ef4444", fontWeight: "600", marginTop: "4px" }}>
                        ❌ Username not available
                      </span>
                    )}
                    {!isCheckingUsername && usernameStatus === "available" && profileData.username.trim().length >= 3 && (
                      <span style={{ fontSize: "12px", color: "#22c55e", fontWeight: "600", marginTop: "4px" }}>
                        ✅ Username available!
                      </span>
                    )}
                  </div>

                  <div className="up-input-group">
                    <label className="up-label">Role</label>
                    <select 
                      value={profileData.role} 
                      onChange={(e) => setProfileData({...profileData, role: e.target.value})}
                      className="up-input"
                    >
                      <option value="Student">Student</option>
                      <option value="Mentor">Mentor / Educator</option>
                      <option value="Professional">Professional</option>
                      <option value="Researcher">Researcher</option>
                    </select>
                  </div>

                  <div className="up-input-group">
                    <label className="up-label">Institution / Organization</label>
                    <input 
                      type="text" 
                      value={profileData.institution} 
                      onChange={(e) => setProfileData({...profileData, institution: e.target.value})}
                      className="up-input"
                      placeholder="e.g. Agni Tutor"
                    />
                  </div>

                  <div className="up-input-group">
                    <label className="up-label">Location (City, Country)</label>
                    <input 
                      type="text" 
                      value={profileData.location} 
                      onChange={(e) => setProfileData({...profileData, location: e.target.value})}
                      className="up-input"
                      placeholder="e.g. Coimbatore"
                    />
                  </div>

                  <div className="up-input-group">
                    <label className="up-label">Website</label>
                    <input 
                      type="text" 
                      value={profileData.website} 
                      onChange={(e) => setProfileData({...profileData, website: e.target.value})}
                      className="up-input"
                      placeholder="e.g. https://agnitutor.com"
                    />
                  </div>

                  <div className="up-input-group up-full-width">
                    <label className="up-label">Bio</label>
                    <textarea 
                      value={profileData.bio} 
                      onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                      rows={3}
                      className="up-input up-textarea"
                      placeholder="e.g. NEET & JEE Mentor passionate about teaching physics..."
                    />
                  </div>
                </div>

                <div className="up-button-row">
                  <button 
                    type="submit" 
                    disabled={isSaving || usernameStatus === "taken"} 
                    className="up-primary-btn"
                    style={{ opacity: usernameStatus === "taken" ? 0.6 : 1, cursor: usernameStatus === "taken" ? "not-allowed" : "pointer" }}
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setIsEditing(false);
                      setUsernameStatus("available");
                    }} 
                    className="up-secondary-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div>
                {profileSubTab === "overview" && (
                  <div className="up-details-section">
                    <h3 className="up-section-title">Account Details</h3>
                    
                    <div className="up-info-grid">
                      <div className="up-info-card">
                        <span className="up-info-label">Email Address</span>
                        <span className="up-info-value">{profileData.email || "Not provided"}</span>
                      </div>

                      <div className="up-info-card">
                        <span className="up-info-label">Institution / Work</span>
                        <span className="up-info-value">{profileData.institution || "Not set"}</span>
                      </div>

                      <div className="up-info-card">
                        <span className="up-info-label">Location</span>
                        <span className="up-info-value">{profileData.location || "Not set"}</span>
                      </div>

                      <div className="up-info-card">
                        <span className="up-info-label">Member Since</span>
                        <span className="up-info-value">{formatDate(profileData.joinedAt)}</span>
                      </div>

                      <div className="up-info-card">
                        <span className="up-info-label">Last Active</span>
                        <span className="up-info-value">{formatDate(profileData.lastLogin)}</span>
                      </div>

                      <div className="up-info-card">
                        <span className="up-info-label">Last Profile Update</span>
                        <span className="up-info-value">{formatDate(profileData.updatedAt)}</span>
                      </div>
                    </div>

                    {/* Badges Display Section */}
                    <div className="up-badges-container">
                      <h3 className="up-section-title">Badges & Accomplishments</h3>
                      {profileData.badges.length === 0 ? (
                        <p className="up-empty-subtext">No badges earned yet. Keep learning and interacting to unlock achievements!</p>
                      ) : (
                        <div className="up-badges-list">
                          {profileData.badges.map((badge, idx) => (
                            <span key={idx} className="up-badge-chip">
                              🎖️ {badge}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {profileSubTab === "followers" && (
                  <div className="up-details-section">
                    <h3 className="up-section-title">Followers</h3>
                    {followersList.length === 0 ? (
                        <div className="up-empty-state">
                            <p style={{ margin: 0, color: "#64748b" }}>No followers yet.</p>
                        </div>
                        ) : (
                        <div className="up-user-list">
                            {followersList.slice(0, visibleFollowersCount).map(user => (
                            <UserCard key={user.id} user={user} currentUser={currentUser} onToggleFollow={handleToggleFollow} />
                            ))}

                            {visibleFollowersCount < followersList.length && (
                            <div style={{ textAlign: "center", marginTop: "16px" }}>
                                <button 
                                onClick={() => setVisibleFollowersCount(prev => prev + 10)} 
                                className="up-secondary-btn"
                                style={{ width: "100%" }}
                                >
                                Load More Followers
                                </button>
                            </div>
                            )}
                        </div>
                        )}
                  </div>
                )}

                {profileSubTab === "following" && (
                  <div className="up-details-section">
                    <h3 className="up-section-title">Following</h3>
                    {followingList.length === 0 ? (
                        <div className="up-empty-state">
                            <p style={{ margin: 0, color: "#64748b" }}>Not following anyone yet.</p>
                        </div>
                        ) : (
                        <div className="up-user-list">
                            {followingList.slice(0, visibleFollowingCount).map(user => (
                            <UserCard key={user.id} user={user} currentUser={currentUser} onToggleFollow={handleToggleFollow} />
                            ))}

                            {visibleFollowingCount < followingList.length && (
                            <div style={{ textAlign: "center", marginTop: "16px" }}>
                                <button 
                                onClick={() => setVisibleFollowingCount(prev => prev + 10)} 
                                className="up-secondary-btn"
                                style={{ width: "100%" }}
                                >
                                Load More Following
                                </button>
                            </div>
                            )}
                        </div>
                        )}
                  </div>
                )}
                {profileSubTab === "battles" && (
                <QuizBattlesTab 
                    currentUser={currentUser} 
                    followingList={followingList} 
                    followersList={followersList} 
                    showToast={showToast} 
                />
                )}
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Discover & Search Peers */}
        {activeTab === "discover" && (
          <div className="up-panel-card">
            <h2 className="up-panel-title">Find Learning Peers</h2>
            
            <form onSubmit={handleSearch} className="up-search-bar-container">
              <input 
                type="text" 
                placeholder="Search peers by username or name..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="up-search-input"
              />
              <button type="submit" className="up-primary-btn">Search</button>
            </form>

            {searchResults.length > 0 && (
              <div style={{ marginBottom: "32px" }}>
                <h3 className="up-section-sub-heading">Search Results</h3>
                <div className="up-user-list">
                  {searchResults.map(user => (
                    <UserCard key={user.id} user={user} currentUser={currentUser} onToggleFollow={handleToggleFollow} />
                  ))}
                </div>
              </div>
            )}

            <h3 className="up-section-sub-heading">Recommended For You</h3>          

                {suggestions.length === 0 ? (
                <div className="up-empty-state">
                    <p style={{ margin: 0, color: "#64748b" }}>
                    No matching profiles found yet. Try updating your location or institution in your profile tab to match with nearby peers!
                    </p>
                </div>
                ) : (
                <div className="up-user-list">
                    {/* Slice the array to only display up to `visibleCount` records */}
                    {suggestions.slice(0, visibleCount).map(user => (
                    <UserCard key={user.id} user={user} currentUser={currentUser} onToggleFollow={handleToggleFollow} />
                    ))}

                    {/* Load More Button */}
                    {visibleCount < suggestions.length && (
                    <div style={{ textAlign: "center", marginTop: "16px" }}>
                        <button 
                        onClick={handleLoadMore} 
                        disabled={loadingMore}
                        className="up-secondary-btn"
                        style={{ width: "100%" }}
                        >
                        {loadingMore ? "Loading..." : "Load More Profiles"}
                        </button>
                    </div>
                    )}
                </div>
                )}
          </div>
        )}

        {/* Mobile History Drawer */}
        <MobileHistoryPanel
          showHistoryMobile={mobileHistoryState}
          sessions={sessions}
          currentSessionId={currentSessionId}
          confirmingDelete={confirmingDelete}
          setConfirmingDelete={setConfirmingDelete}
          setShowHistoryMobile={setShowHistoryMobile}
          openSession={openSession}
          deleteSession={deleteSession}
          groupSessionsByDate={groupSessionsByDate}
          lastUserQuestionSafe={lastUserQuestionSafe}
          getSubjectIcon={getSubjectIcon}
          showSocialMenu={showSocialMenu}
          setShowSocialMenu={setShowSocialMenu}
          socialMenuRef={socialMenuRef}
          setShowFeedback={setShowFeedback}
          newSessions={newSessions}  
          openDeleteAllModal={openDeleteAllModal}  
          confirmDeleteAll={confirmDeleteAll}     
          closeDeleteAllModal={closeDeleteAllModal} 
          DangerModal={DangerModal}
          currentUser={currentUser}
          setActiveTab={setActiveTab}         
          setProfileSubTab={setProfileSubTab}
        />
      </main>

      {/* Instagram-style Floating Chat Window Drawer */}
      

      {/* Modern Toast Notification */}
      {notification.show && (
        <div className={`up-toast-wrapper ${notification.type}`}>
          <div className="up-toast-icon-box">
            {notification.type === "success" ? "✨" : "⚠️"}
          </div>
          <div className="up-toast-content">
            <h4 className="up-toast-heading">{notification.type === "success" ? "Success" : "Notice"}</h4>
            <p className="up-toast-message">{notification.message}</p>
          </div>
          <button 
            onClick={() => setNotification(prev => ({ ...prev, show: false }))} 
            className="up-toast-dismiss"
            aria-label="Close"
          >
            &times;
          </button>
          <div className="up-toast-progress-bar"></div>
        </div>
      )}
    </div>
  );
}

// User Card Sub-component
function UserCard({ user, currentUser, onToggleFollow }) {
  const isFollowing = user.followers?.includes(currentUser?.uid);
  const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    user.name || user.username || "User"
  )}&background=ea580c&color=fff`;

  return (
    <div className="up-user-card">
      <div className="up-user-card-left">
        <img 
          src={user.photoURL || fallbackAvatar} 
          alt={user.name || "User"} 
          referrerPolicy="no-referrer"
          className="up-card-avatar"
          onError={(e) => { e.target.src = fallbackAvatar; }}
        />
        <div className="up-user-info">
          <div className="up-card-title-row">
            <span className="up-card-name">{user.name || `@${user.username}`}</span>
            <span className="up-card-username">@{user.username || "user"}</span>
            {user.role && <span className="up-card-card-role-badge" style={{ backgroundColor: "#fff7ed", color: "#ea580c", fontSize: "10px", padding: "2px 6px", borderRadius: "8px", fontWeight: "600" }}>{user.role}</span>}
          </div>
          {user.bio && <p className="up-card-bio">{user.bio}</p>}
          <div className="up-card-meta">
            📍 {user.location || "Location unlisted"} &nbsp;•&nbsp; 🏫 {user.institution || "Institution unlisted"}
          </div>
        </div>
      </div>

      <button 
        onClick={() => onToggleFollow(user.id, isFollowing)}
        className={isFollowing ? "up-unfollow-btn" : "up-follow-btn"}
      >
        {isFollowing ? "Following" : "+ Follow"}
      </button>
    </div>
  );
}